import { Ref } from 'vue';

import { CommentService, useComments } from '@rygen/rygen-ui/composables';
import {
  UserComment,
  UserCommentSearchFilter,
  UserCommentSearchResult,
  UserCommentWithRepliesSearchResult,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useCurrentUserStore } from '@/stores/CurrentUser.ts';

class OrderCommentService implements CommentService {
  constructor(private orderId: Ref<number>) {}

  async loadComments(
    searchQuery: UserCommentSearchFilter,
  ): Promise<UserCommentWithRepliesSearchResult> {
    return api.orderComment.findCommentsForOrder(
      this.orderId.value,
      searchQuery,
    );
  }

  async createComment(content: Record<string, unknown>): Promise<UserComment> {
    return api.orderComment.createComment(this.orderId.value, content);
  }

  async updateComment(
    commentId: number,
    content: Record<string, unknown>,
  ): Promise<UserComment> {
    return api.orderComment.updateComment(
      this.orderId.value,
      commentId,
      content,
    );
  }

  async replyToComment(
    commentId: number,
    content: Record<string, unknown>,
  ): Promise<UserComment> {
    return api.orderComment.replyToComment(
      this.orderId.value,
      commentId,
      content,
    );
  }

  async deleteComment(commentId: number): Promise<void> {
    return api.orderComment.deleteComment(this.orderId.value, commentId);
  }

  async loadReplies(
    commentId: number,
    searchQuery: UserCommentSearchFilter,
  ): Promise<UserCommentSearchResult> {
    return api.orderComment.findRepliesForComment(
      this.orderId.value,
      commentId,
      searchQuery,
    );
  }

  async loadCommentsCount(): Promise<number> {
    // Order comments don't use a total count
    return -1;
  }
}

export const useOrderComments = (orderId: Ref<number>) => {
  const { handleRequiredDomainRedirect } = useCurrentUserStore();

  const commentService = new OrderCommentService(orderId);

  return useComments(
    commentService,
    orderId,
    'Order',
    handleRequiredDomainRedirect,
  );
};
